import type { ReactNode } from "react";

export type Colonne<T> = {
  readonly cle: string;
  readonly titre: string;
  /** Les montants s'alignent à droite, comme sur les livrables. */
  readonly montant?: boolean;
  readonly rendu: (ligne: T) => ReactNode;
};

/**
 * Tableau de données. Une ligne dont `total` est vrai est surlignée or pâle
 * et mise en gras — même convention que les livrables à la charte (§5.M).
 */
export function Tableau<T>({
  colonnes,
  lignes,
  cle,
  total,
  vide = "Aucune donnée.",
  surLigne,
}: {
  colonnes: readonly Colonne<T>[];
  lignes: readonly T[];
  cle: (ligne: T, index: number) => string;
  total?: (ligne: T) => boolean;
  vide?: string;
  surLigne?: (ligne: T) => void;
}) {
  if (lignes.length === 0) return <p className="a-faire">{vide}</p>;

  return (
    <div className="tableau-cadre">
      <table className="tableau">
        <thead>
          <tr>
            {colonnes.map((c) => (
              <th key={c.cle} className={c.montant ? "droite" : undefined}>
                {c.titre}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => (
            <tr
              key={cle(l, i)}
              className={total?.(l) ? "ligne-total" : undefined}
              onClick={surLigne ? () => surLigne(l) : undefined}
              style={surLigne ? { cursor: "pointer" } : undefined}
            >
              {colonnes.map((c) => (
                <td key={c.cle} className={c.montant ? "droite" : undefined}>
                  {c.rendu(l)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Tuiles({ tuiles }: { tuiles: readonly { titre: string; valeur: ReactNode; note?: string; alerte?: boolean }[] }) {
  return (
    <div className="tuiles">
      {tuiles.map((t) => (
        <div key={t.titre} className={`tuile${t.alerte ? " tuile--alerte" : ""}`}>
          <span className="tuile__titre">{t.titre}</span>
          <span className="tuile__valeur">{t.valeur}</span>
          {t.note && <span className="tuile__note">{t.note}</span>}
        </div>
      ))}
    </div>
  );
}

/** Sélecteur année + mois, partagé par tous les écrans mensuels. */
export function SelecteurPeriode({
  annee,
  mois,
  setAnnee,
  setMois,
  avecMois = true,
}: {
  annee: number;
  mois: number;
  setAnnee: (v: number) => void;
  setMois: (v: number) => void;
  avecMois?: boolean;
}) {
  const annees = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
  const moisNoms = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
  ];

  return (
    <div className="periode">
      <label>
        Exercice
        <select value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
          {annees.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </label>

      {avecMois && (
        <label>
          Mois
          <select value={mois} onChange={(e) => setMois(Number(e.target.value))}>
            {moisNoms.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
